import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatDate } from '../../utils/helpers';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Sparkles, Plus, Edit, Trash2, Coins, Settings2, Check, X } from 'lucide-react';

const TASK_TYPES = [
  ['daily_checkin', 'Daily Check-in'], ['watch_video', 'Watch Video'], ['watch_ad', 'Watch Ad'],
  ['visit_website', 'Visit Website'], ['external_link', 'External Link'], ['youtube_subscribe', 'YouTube Subscribe'],
  ['youtube_watch', 'YouTube Watch'], ['tiktok_follow', 'TikTok Follow'], ['tiktok_like', 'TikTok Like'],
  ['facebook_follow', 'Facebook Follow'], ['facebook_like', 'Facebook Like'], ['instagram_follow', 'Instagram Follow'],
  ['telegram_join', 'Telegram Join'], ['whatsapp_join', 'WhatsApp Join'], ['discord_join', 'Discord Join'],
  ['twitter_follow', 'Twitter/X Follow'], ['twitter_retweet', 'Twitter/X Retweet'], ['linkedin_follow', 'LinkedIn Follow'],
  ['quiz', 'Quiz'], ['survey', 'Survey'], ['referral', 'Referral'],
  ['upload_screenshot', 'Upload Screenshot'], ['manual', 'Manual Verification'], ['custom', 'Custom'],
];
const VERIFICATION = [
  ['automatic', 'Automatic'], ['button_click', 'Button Click'], ['timer', 'Timer Based'],
  ['screenshot', 'Screenshot Required'], ['manual', 'Manual'], ['admin_approval', 'Admin Approval'],
];

const EMPTY_PACK = {
  name: '', description: '', price: 100, currency: 'ZAR', coins_issued: 500, duration_days: 30,
  max_daily_earnings: 500, max_total_earnings: 0, max_tasks_per_day: 20,
  min_withdrawal: 100, active: true, badge: '', featured: false, sort_order: 0,
};
const EMPTY_TASK = {
  title: '', description: '', instructions: '', reward_coins: 10, task_type: 'daily_checkin',
  category: 'general', difficulty: 'easy', url: '', eligible_pack_ids: [],
  verification_mode: 'automatic', daily_limit: 1, completion_limit: 0, estimated_seconds: 30,
  status: 'active', featured: false, sort_order: 0,
};

export const AdminTaskingPage = () => {
  const [cfg, setCfg] = useState({ coin_name: '', coin_symbol: '', exchange_rates: {}, allow_multiple_active_packs: false });
  const [overview, setOverview] = useState(null);
  const [packs, setPacks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packForm, setPackForm] = useState(null);
  const [taskForm, setTaskForm] = useState(null);
  const [rateForm, setRateForm] = useState({ currency: 'ZAR', rate: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [c, ov, p, t, comp, curs] = await Promise.all([
        adminAPI.getTaskingConfig(),
        adminAPI.getTaskingOverview(),
        adminAPI.listAdminTaskingPacks(),
        adminAPI.listAdminTaskingTasks(),
        adminAPI.listTaskingCompletions('pending').catch(() => ({ data: [] })),
        publicAPI.getCurrencies(true),
      ]);
      setCfg(c.data);
      setOverview(ov.data);
      setPacks(p.data);
      setTasks(t.data);
      setCompletions(comp.data);
      setCurrencies(curs.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    try {
      await adminAPI.updateTaskingConfig({
        coin_name: cfg.coin_name, coin_symbol: cfg.coin_symbol,
        allow_multiple_active_packs: cfg.allow_multiple_active_packs,
      });
      toast.success('Coin config updated');
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const setRate = async () => {
    if (!rateForm.currency || !rateForm.rate) return toast.error('Currency and rate required');
    const newRates = { ...(cfg.exchange_rates || {}), [rateForm.currency]: parseFloat(rateForm.rate) };
    try {
      await adminAPI.updateTaskingConfig({ exchange_rates: newRates });
      toast.success('Rate updated');
      setRateForm({ currency: 'ZAR', rate: '' });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const removeRate = async (cur) => {
    const { [cur]: _, ...rest } = cfg.exchange_rates || {};
    try {
      await adminAPI.updateTaskingConfig({ exchange_rates: rest });
      toast.success('Removed');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const savePack = async () => {
    if (!packForm.name) return toast.error('Name required');
    try {
      if (packForm.id) await adminAPI.updateTaskingPack(packForm.id, packForm);
      else await adminAPI.createTaskingPack(packForm);
      toast.success('Saved');
      setPackForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const deletePack = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await adminAPI.deleteTaskingPack(p.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const saveTask = async () => {
    if (!taskForm.title) return toast.error('Title required');
    try {
      if (taskForm.id) await adminAPI.updateTaskingTask(taskForm.id, taskForm);
      else await adminAPI.createTaskingTask(taskForm);
      toast.success('Saved');
      setTaskForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const deleteTask = async (t) => {
    if (!window.confirm(`Delete ${t.title}?`)) return;
    try { await adminAPI.deleteTaskingTask(t.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const approveCompletion = async (c) => {
    try { await adminAPI.approveCompletion(c.id); toast.success('Approved'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const rejectCompletion = async (c) => {
    const reason = window.prompt('Reason?');
    if (!reason) return;
    try { await adminAPI.rejectCompletion(c.id, reason); toast.success('Rejected'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-tasking-page">
        <div>
          <h1 className="text-4xl font-semibold flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Sparkles className="h-8 w-8 text-amber-500" /> Tasking &amp; Rewards
          </h1>
          <p className="mt-2 text-muted-foreground">Manage the reward coin, packs, tasks and pending verifications</p>
        </div>

        {overview && (
          <div className="grid gap-3 md:grid-cols-5">
            {[
              { l: 'Active packs', v: overview.packs_active },
              { l: 'Users with pack', v: overview.users_with_active_pack },
              { l: 'Pending reviews', v: overview.pending_completions },
              { l: `Coins earned (lifetime)`, v: overview.total_coins_earned_lifetime?.toFixed(0) },
              { l: 'Coins withdrawn', v: overview.total_coins_withdrawn?.toFixed(0) },
            ].map(s => (
              <Card key={s.l}><CardContent className="pt-4"><p className="text-xs uppercase text-muted-foreground">{s.l}</p><p className="text-2xl font-semibold mt-1">{s.v}</p></CardContent></Card>
            ))}
          </div>
        )}

        <Tabs defaultValue="config">
          <TabsList>
            <TabsTrigger value="config" data-testid="tab-config"><Settings2 className="h-4 w-4 mr-2" /> Coin Config</TabsTrigger>
            <TabsTrigger value="packs" data-testid="tab-packs">Packs ({packs.length})</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks ({tasks.length})</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">
              Reviews ({completions.length})
              {completions.length > 0 && <span className="ml-2 h-2 w-2 rounded-full bg-amber-500 animate-pulse" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="config">
            <Card>
              <CardHeader><CardTitle>Reward Coin</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <div><Label>Coin name</Label><Input value={cfg.coin_name || ''} onChange={(e) => setCfg({ ...cfg, coin_name: e.target.value })} placeholder="Matrise Points" data-testid="coin-name-input" /></div>
                  <div><Label>Symbol</Label><Input value={cfg.coin_symbol || ''} onChange={(e) => setCfg({ ...cfg, coin_symbol: e.target.value.toUpperCase() })} placeholder="RWP" data-testid="coin-symbol-input" /></div>
                  <div className="flex items-end gap-2">
                    <div className="flex items-center gap-2">
                      <Switch checked={cfg.allow_multiple_active_packs} onCheckedChange={(v) => setCfg({ ...cfg, allow_multiple_active_packs: v })} />
                      <Label>Allow multiple active packs</Label>
                    </div>
                  </div>
                </div>
                <Button onClick={saveConfig} data-testid="save-config-btn">Save Config</Button>

                <div className="pt-4 border-t">
                  <p className="font-medium mb-3">Exchange Rates (1 {cfg.coin_symbol || 'TC'} = X currency)</p>
                  <div className="grid gap-2 mb-3">
                    {Object.entries(cfg.exchange_rates || {}).map(([cur, rate]) => (
                      <div key={cur} className="flex items-center gap-3 p-3 rounded-lg border border-border" data-testid={`rate-${cur}`}>
                        <span className="font-medium">1 {cfg.coin_symbol || 'TC'}</span>
                        <span className="text-muted-foreground">=</span>
                        <span className="font-mono">{rate} {cur}</span>
                        <Button size="icon" variant="ghost" onClick={() => removeRate(cur)} className="ml-auto"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    {Object.keys(cfg.exchange_rates || {}).length === 0 && <p className="text-xs text-muted-foreground">No rates configured. Users cannot withdraw until you set one.</p>}
                  </div>
                  <div className="flex gap-2">
                    <Select value={rateForm.currency} onValueChange={(v) => setRateForm({ ...rateForm, currency: v })}>
                      <SelectTrigger className="w-32" data-testid="rate-currency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{currencies.map(c => <SelectItem key={c.symbol} value={c.symbol}>{c.symbol}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" step="0.001" placeholder="Rate (e.g. 0.10)" value={rateForm.rate} onChange={(e) => setRateForm({ ...rateForm, rate: e.target.value })} data-testid="rate-value-input" />
                    <Button onClick={setRate} data-testid="set-rate-btn"><Plus className="h-4 w-4 mr-1" /> Set</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="packs">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between"><CardTitle>Packs</CardTitle>
                  <Button onClick={() => setPackForm({ ...EMPTY_PACK, currency: currencies[0]?.symbol || 'ZAR' })} data-testid="new-pack-btn"><Plus className="h-4 w-4 mr-2" /> New Pack</Button>
                </div>
              </CardHeader>
              <CardContent>
                {packs.length === 0 ? <p className="text-center py-8 text-muted-foreground">No packs yet.</p> : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Name</TableHead><TableHead>Price</TableHead><TableHead>Coins</TableHead>
                      <TableHead>Days</TableHead><TableHead>Daily max</TableHead><TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {packs.map(p => (
                        <TableRow key={p.id} data-testid={`pack-row-${p.id}`}>
                          <TableCell className="font-medium">{p.name} {p.badge && <Badge variant="outline" className="ml-2">{p.badge}</Badge>}</TableCell>
                          <TableCell>{p.price} {p.currency}</TableCell>
                          <TableCell>{p.coins_issued}</TableCell>
                          <TableCell>{p.duration_days}</TableCell>
                          <TableCell>{p.max_daily_earnings || '—'}</TableCell>
                          <TableCell><Badge variant="outline" className={p.active ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' : 'bg-muted'}>{p.active ? 'Active' : 'Inactive'}</Badge></TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => setPackForm({ ...p })} data-testid={`edit-pack-${p.id}`}><Edit className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => deletePack(p)}><Trash2 className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tasks">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between"><CardTitle>Tasks</CardTitle>
                  <Button onClick={() => setTaskForm({ ...EMPTY_TASK })} data-testid="new-task-btn"><Plus className="h-4 w-4 mr-2" /> New Task</Button>
                </div>
              </CardHeader>
              <CardContent>
                {tasks.length === 0 ? <p className="text-center py-8 text-muted-foreground">No tasks yet.</p> : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Reward</TableHead>
                      <TableHead>Verification</TableHead><TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {tasks.map(t => (
                        <TableRow key={t.id} data-testid={`task-row-${t.id}`}>
                          <TableCell className="font-medium">{t.title}</TableCell>
                          <TableCell className="text-xs">{t.task_type.replace(/_/g, ' ')}</TableCell>
                          <TableCell>+{t.reward_coins}</TableCell>
                          <TableCell className="text-xs">{t.verification_mode}</TableCell>
                          <TableCell><Badge variant="outline" className={t.status === 'active' ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' : 'bg-muted'}>{t.status}</Badge></TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => setTaskForm({ ...t })} data-testid={`edit-task-${t.id}`}><Edit className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => deleteTask(t)}><Trash2 className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reviews">
            <Card>
              <CardContent className="pt-6">
                {completions.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No pending reviews.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>User</TableHead><TableHead>Task</TableHead><TableHead>Reward</TableHead>
                      <TableHead>Mode</TableHead><TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {completions.map(c => (
                        <TableRow key={c.id} data-testid={`review-row-${c.id}`}>
                          <TableCell className="text-sm">{c.user_email}</TableCell>
                          <TableCell>{c.task_title}</TableCell>
                          <TableCell>+{c.reward}</TableCell>
                          <TableCell className="text-xs">{c.verification_mode}</TableCell>
                          <TableCell className="text-xs">{formatDate(c.created_at)}</TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="sm" onClick={() => approveCompletion(c)} data-testid={`approve-${c.id}`}><Check className="h-3 w-3 mr-1" /> Approve</Button>
                            <Button size="sm" variant="destructive" onClick={() => rejectCompletion(c)} data-testid={`reject-${c.id}`}><X className="h-3 w-3 mr-1" /> Reject</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Pack dialog */}
      <Dialog open={!!packForm} onOpenChange={(v) => !v && setPackForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{packForm?.id ? 'Edit Pack' : 'New Pack'}</DialogTitle>
            <DialogDescription>Configure pricing, coins issued, duration and daily limits.</DialogDescription>
          </DialogHeader>
          {packForm && (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2"><Label>Name</Label><Input value={packForm.name} onChange={(e) => setPackForm({ ...packForm, name: e.target.value })} data-testid="pack-name-input" /></div>
              <div className="md:col-span-2"><Label>Description</Label><Textarea rows={2} value={packForm.description} onChange={(e) => setPackForm({ ...packForm, description: e.target.value })} /></div>
              <div><Label>Price</Label><Input type="number" value={packForm.price} onChange={(e) => setPackForm({ ...packForm, price: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Currency</Label>
                <Select value={packForm.currency} onValueChange={(v) => setPackForm({ ...packForm, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{currencies.map(c => <SelectItem key={c.symbol} value={c.symbol}>{c.symbol}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Coins issued at activation</Label><Input type="number" value={packForm.coins_issued} onChange={(e) => setPackForm({ ...packForm, coins_issued: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Duration (days)</Label><Input type="number" value={packForm.duration_days} onChange={(e) => setPackForm({ ...packForm, duration_days: parseInt(e.target.value) || 30 })} /></div>
              <div><Label>Max daily earnings</Label><Input type="number" value={packForm.max_daily_earnings} onChange={(e) => setPackForm({ ...packForm, max_daily_earnings: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Max total earnings (0=∞)</Label><Input type="number" value={packForm.max_total_earnings} onChange={(e) => setPackForm({ ...packForm, max_total_earnings: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Max tasks/day</Label><Input type="number" value={packForm.max_tasks_per_day} onChange={(e) => setPackForm({ ...packForm, max_tasks_per_day: parseInt(e.target.value) || 0 })} /></div>
              <div><Label>Min withdrawal</Label><Input type="number" value={packForm.min_withdrawal} onChange={(e) => setPackForm({ ...packForm, min_withdrawal: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Badge (e.g. gold, vip)</Label><Input value={packForm.badge || ''} onChange={(e) => setPackForm({ ...packForm, badge: e.target.value })} /></div>
              <div><Label>Sort order</Label><Input type="number" value={packForm.sort_order} onChange={(e) => setPackForm({ ...packForm, sort_order: parseInt(e.target.value) || 0 })} /></div>
              <div className="flex items-center gap-2 md:col-span-2 pt-2">
                <Switch checked={packForm.active} onCheckedChange={(v) => setPackForm({ ...packForm, active: v })} /><Label>Active</Label>
                <div className="flex items-center gap-2 ml-4"><Switch checked={packForm.featured} onCheckedChange={(v) => setPackForm({ ...packForm, featured: v })} /><Label>Featured</Label></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPackForm(null)}>Cancel</Button>
            <Button onClick={savePack} data-testid="save-pack-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task dialog */}
      <Dialog open={!!taskForm} onOpenChange={(v) => !v && setTaskForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{taskForm?.id ? 'Edit Task' : 'New Task'}</DialogTitle>
            <DialogDescription>Give it a reward, choose type + verification mode, and optionally attach a URL.</DialogDescription>
          </DialogHeader>
          {taskForm && (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2"><Label>Title</Label><Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} data-testid="task-title-input" /></div>
              <div className="md:col-span-2"><Label>Description / Instructions</Label><Textarea rows={2} value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} /></div>
              <div>
                <Label>Task type</Label>
                <Select value={taskForm.task_type} onValueChange={(v) => setTaskForm({ ...taskForm, task_type: v })}>
                  <SelectTrigger data-testid="task-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Verification</Label>
                <Select value={taskForm.verification_mode} onValueChange={(v) => setTaskForm({ ...taskForm, verification_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{VERIFICATION.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Reward (coins)</Label><Input type="number" value={taskForm.reward_coins} onChange={(e) => setTaskForm({ ...taskForm, reward_coins: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Daily limit</Label><Input type="number" value={taskForm.daily_limit} onChange={(e) => setTaskForm({ ...taskForm, daily_limit: parseInt(e.target.value) || 1 })} /></div>
              <div><Label>Completion cap (0=∞)</Label><Input type="number" value={taskForm.completion_limit} onChange={(e) => setTaskForm({ ...taskForm, completion_limit: parseInt(e.target.value) || 0 })} /></div>
              <div><Label>Estimated seconds</Label><Input type="number" value={taskForm.estimated_seconds} onChange={(e) => setTaskForm({ ...taskForm, estimated_seconds: parseInt(e.target.value) || 0 })} /></div>
              <div className="md:col-span-2"><Label>URL (optional — YouTube, TikTok, form, etc.)</Label><Input value={taskForm.url || ''} onChange={(e) => setTaskForm({ ...taskForm, url: e.target.value })} placeholder="https://" /></div>
              <div><Label>Category</Label><Input value={taskForm.category || ''} onChange={(e) => setTaskForm({ ...taskForm, category: e.target.value })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={taskForm.status} onValueChange={(v) => setTaskForm({ ...taskForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="scheduled">Scheduled</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Eligible packs (leave empty for all)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {packs.map(p => (
                    <button key={p.id} type="button" onClick={() => {
                      const set = new Set(taskForm.eligible_pack_ids || []);
                      if (set.has(p.id)) set.delete(p.id); else set.add(p.id);
                      setTaskForm({ ...taskForm, eligible_pack_ids: Array.from(set) });
                    }}
                    className={`text-xs px-3 py-1 rounded-full border ${(taskForm.eligible_pack_ids || []).includes(p.id) ? 'bg-amber-500/20 border-amber-500 text-amber-700' : 'border-border text-muted-foreground'}`}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <Switch checked={taskForm.featured} onCheckedChange={(v) => setTaskForm({ ...taskForm, featured: v })} /><Label>Featured</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskForm(null)}>Cancel</Button>
            <Button onClick={saveTask} data-testid="save-task-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
